import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import api, { apiError } from "@/lib/api";
import AuthShell from "@/pages/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Check, Circle } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [expired, setExpired] = useState(false);
  const [resendEmail, setResendEmail] = useState("");
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const rules = [
    { label: t("auth.pwMin"), ok: password.length >= 8 },
    { label: t("auth.pwLetter"), ok: /[a-zA-Z]/.test(password) },
    { label: t("auth.pwNumber"), ok: /[0-9]/.test(password) },
  ];
  const matches = confirm.length > 0 && password === confirm;
  const allValid = rules.every((r) => r.ok) && matches;

  const submit = async (e) => {
    e.preventDefault();
    if (!allValid) return;
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success(t("auth.passwordUpdated"));
      navigate("/login");
    } catch (err) {
      const detail = apiError(err);
      if (err?.response?.status === 400 && /reset link/i.test(detail)) {
        setExpired(true);
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  const resend = async (e) => {
    e.preventDefault();
    setResending(true);
    try {
      await api.post("/auth/forgot-password", { email: resendEmail });
      setResent(true);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setResending(false);
    }
  };

  if (expired) {
    return (
      <AuthShell title={t("auth.linkExpiredTitle")} subtitle={t("auth.linkExpiredDesc")}>
        {resent ? (
          <div data-testid="reset-resent" className="text-sm text-slatebody">
            {t("auth.resetSent", { email: resendEmail })}
            <div className="mt-4"><Link to="/login" className="text-[#202123] hover:underline">{t("auth.backToLogin")}</Link></div>
          </div>
        ) : (
          <form onSubmit={resend} className="space-y-4" data-testid="reset-expired-form">
            <div>
              <Label htmlFor="resend-email">{t("auth.email")}</Label>
              <Input id="resend-email" type="email" required value={resendEmail} onChange={(e) => setResendEmail(e.target.value)} className="mt-1.5" data-testid="reset-resend-email" />
            </div>
            <Button type="submit" disabled={resending} className="w-full bg-[#ECECEC] hover:bg-[#E0E0E0] text-[#202123] border border-[#C5C5D0]" data-testid="reset-resend-submit">
              {resending ? t("auth.sending") : t("auth.sendNewLink")}
            </Button>
            <p className="text-sm text-slatebody text-center"><Link to="/login" className="text-[#202123] hover:underline">{t("auth.backToLogin")}</Link></p>
          </form>
        )}
      </AuthShell>
    );
  }

  return (
    <AuthShell title={t("auth.setNewPassword")} subtitle={t("auth.resetSubtitle2")}>
      {!token ? (
        <p className="text-sm text-slatebody" data-testid="reset-invalid">
          {t("auth.invalidReset")} <Link to="/forgot-password" className="text-[#202123] hover:underline">{t("auth.requestNew")}</Link>.
        </p>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
          <div>
            <Label htmlFor="password">{t("auth.newPassword")}</Label>
            <PasswordInput id="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5" data-testid="reset-password" />
            <ul className="mt-3 space-y-1.5" data-testid="password-rules">
              {rules.map((r, i) => (
                <li key={i} className={`flex items-center gap-2 text-xs transition-colors ${r.ok ? "text-[#202123]" : "text-slatebody"}`} data-testid={`password-rule-${i}${r.ok ? "-ok" : ""}`}>
                  {r.ok ? <Check className="h-3.5 w-3.5" /> : <Circle className="h-3 w-3" />}
                  {r.label}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <Label htmlFor="confirm">{t("auth.confirmPassword")}</Label>
            <PasswordInput id="confirm" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1.5" data-testid="reset-confirm" />
            {confirm.length > 0 && !matches && <p className="text-xs text-red-600 mt-1.5" data-testid="reset-mismatch">{t("auth.mismatch")}</p>}
          </div>
          <Button type="submit" disabled={loading || !allValid} className="w-full bg-[#ECECEC] hover:bg-[#E0E0E0] text-[#202123] border border-[#C5C5D0]" data-testid="reset-submit">
            {loading ? t("auth.updating") : t("auth.updatePassword")}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}
